/**
 * @typedef {Object} HistoryEntry
 * @property {string} url The URL.
 * @property {number} dateAdded The timestamp of when this entry was added.
 * @property {number} dateUpdated The timestamp of when this entry was last updated.
 */

/**
 * The maximum number of history entries to store.
 * @type {number}
 */
const HISTORY_ENTRY_LIMIT = 20;

/**
 * The key to use when storing and retrieving data.
 * @type {string}
 */
const HISTORY_STORAGE_KEY = 'return';

export let HistoryManager = create();

function create() {
	/**
	 * Parses a URL, returning null on failure rather than throwing.
	 *
	 * @param {string} url
	 * @param {string} base
	 * @returns {URL|null}
	 */
	function parseUrl(url, base) {
		try {
			return new URL(url, base);
		} catch (error) {
			return null;
		}
	}

	/**
	 * Reads the history from storage and returns the decoded value.
	 *
	 * @returns {HistoryEntry[]}
	 */
	function getHistory() {
		let data = sessionStorage.getItem(HISTORY_STORAGE_KEY);

		if (data === null) {
			return [];
		}

		let decodedData;

		try {
			decodedData = JSON.parse(data);
		} catch (error) {
			return [];
		}

		return decodedData;
	}

	/**
	 * Clears the history from storage.
	 */
	function clearHistory() {
		writeHistory([]);
	}

	/**
	 * @param {HistoryEntry[]} history
	 */
	function writeHistory(history) {
		if (history.length > HISTORY_ENTRY_LIMIT) {
			history = history.slice(HISTORY_ENTRY_LIMIT * -1);
		}

		try {
			sessionStorage.setItem(HISTORY_STORAGE_KEY, JSON.stringify(history));
		} catch (error) {}
	}

	/**
	 * Creates a new HistoryEntry with the given URL and stores it.
	 * @param {string} url
	 */
	function addUrlToHistory(url) {
		let history = getHistory();

		let currentTimestamp = getCurrentTimestamp();

		let entry = {
			url: url,
			dateAdded: currentTimestamp,
			dateUpdated: currentTimestamp,
		};

		history.push(entry)

		writeHistory(history);
	}

	/**
	 * Updates the last entry with the current URL.
	 */
	function updateLastEntry() {
		let history = getHistory();

		let lastEntry = history.pop();

		if (!lastEntry) {
			return addUrlToHistory(getCurrentUrl());
		}

		lastEntry.url = getCurrentUrl();
		lastEntry.dateUpdated = getCurrentTimestamp();

		history.push(lastEntry);

		writeHistory(history);
	}

	/**
	 * Writes the current URL to history and observes for changes.
	 */
	function observeHistory() {
		let history = getHistory();
		let lastEntry = history.pop();
		let currentUrl = getCurrentUrl();

		/**
		 * If we have an existing HistoryEntry and the pathname matches the current pathname, then
		 * update the existing entry. This prevents duplicates in scenarios such as the page being
		 * refreshed.
		 */
		if (lastEntry) {
			let parsedLastEntry = parseUrl(lastEntry.url, window.location.origin);

			if (parsedLastEntry && window.location.pathname === parsedLastEntry.pathname) {
				updateLastEntry();
			} else {
				addUrlToHistory(currentUrl);
			}
		} else {
			addUrlToHistory(currentUrl);
		}

		/**
		 * We want to keep the current HistoryEntry up to date with changes made by replaceState etc.
		 * Unfortunately there isn't yet a widely supported way to do so that doesn't involve overriding
		 * methods such as window.replaceSate. Therefore, instead we update the URL when the user navigates
		 * away.
		 */
		window.addEventListener('beforeunload', () => {
			updateLastEntry();
		});
	}

	/**
	 * Finds the most recent HistoryEntry that matches one of the provided RegEx patterns.
	 *
	 * @param {RegExp[]} patterns
	 * @returns {HistoryEntry|null}
	 */
	function findLastEntry(patterns) {
		let entry = getHistory().reverse().find(entry => {
			let parsed = parseUrl(entry.url, window.location.origin);

			if (!parsed) {
				return false;
			}

			return patterns.some(pattern => parsed.pathname.match(pattern));
		});

		return entry ?? null;
	}

	/**
	 * Redirects to the given URL, taking care of ensuring the origin matches and guarding against XSS.
	 *
	 * @param {string} url The URL to redirect to relative to the current origin.
	 * @param {boolean} replace Whether to replace the current entry in the browsers' history.
	 */
	function redirect(url, replace = true) {
		if (!url) {
			throw new Error('Required parameter “url” was not provided.');
		}

		let currentOrigin = window.location.origin;

		/**
		 * new URL will take care of encoding preventing XSS from malicious URLs such as javascript:alert('XSS');
		 * In theory provided the URL has come from storage this shouldn't be possible unless the stored value
		 * has been manipulated somehow, or we've stored a bad URL from user input.
		 */
		let parsed = parseUrl(url, currentOrigin);

		if (!parsed) {
			return;
		}

		let { href, origin } = parsed;

		/**
		 * Check the origin matches, if not something fishy could be going on in which case we will return early.
		 */
		if (origin !== currentOrigin) {
			return;
		}

		if (replace) {
			history.replaceState({}, "", href);
		}

		window.location.href = href;
	}

	/**
	 * Gets the current timestamp.
	 * @returns {number}
	 */
	function getCurrentTimestamp() {
		return (new Date()).getTime();
	}

	/**
	 * Gets the pathname and query parameters of the current URL.
	 *
	 * @returns {string}
	 */
	function getCurrentUrl() {
		let { pathname, search } = window.location;

		return [ pathname, search ].filter(Boolean).join('');
	}

	return {
		getHistory,
		clearHistory,
		observeHistory,
		findLastEntry,
		redirect,
	};
}
